import { Component, inject, signal, computed, effect, input } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { COLLECTION_PATIENTS, COLLECTION_STUDENT_PATIENTS } from 'src/app/calvin-emr-shared/global';
import { PATH_RELEASE_PAGES, PATH_MORE_PROVIDER_ORDER_IDS } from 'src/app/global';
import { PageComponent } from 'src/app/calvin-emr-shared/page/page.component';
import { PatientService } from 'src/app/calvin-emr-shared/services/patient.service';
import { Patient } from 'src/app/calvin-emr-shared/models/patient.model';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { ContainersService } from 'src/app/knightmeds/services/containers.service';
import { ProgressBar } from 'primeng/progressbar';
import { DataView } from 'primeng/dataview';
import { Checkbox } from 'primeng/checkbox';
import { NgClass } from '@angular/common';
import { ButtonDirective } from 'primeng/button';

/** A page to select the provider orders to release for a patient */
@Component({
  selector: 'app-medications-page',
  templateUrl: './medications-page.component.html',
  styleUrls: ['./medications-page.component.scss'],
  standalone: true,
  imports: [
    ProgressBar,
    DataView,
    RouterLink,
    Checkbox,
    NgClass,
    ReactiveFormsModule,
    ButtonDirective,
  ]
})
export class MedicationsPageComponent extends PageComponent {
  /** The patient to list provider orders for */
  private patientSignal = signal<Patient | null>(null);
  readonly patient = computed(() => this.patientSignal());

  /** A list of the ids of any selected provider orders */
  providerOrderSelections = new FormControl<string[]>([]);

  /** A list of the ids of all provider orders where the medication is available */
  private inStockProviderOrdersIdsSignal = signal<string[]>([]);
  readonly inStockProviderOrdersIds = computed(() => this.inStockProviderOrdersIdsSignal());

  /** Whether all necessary data has loaded */
  readonly loaded = computed(() => !!this.patientSignal());

  readonly patientId = input<string>(undefined, { alias: 'patient-id' });

  private router = inject(Router);
  private patientService = inject(PatientService);
  private containersService = inject(ContainersService);

  constructor() {
    super();

    // Wait for user to load, then validate route and fetch patient
    effect(() => {
      const user = this.authStore.user();
      if (!user) return;

      const patientId = this.patientId();

      if (!patientId || !user.patientIds.includes(patientId)) {
        this.router.navigate(['/', PATH_RELEASE_PAGES]);
        return;
      }

      this.patientService.getPatient(patientId, user.isAdmin ? COLLECTION_PATIENTS : COLLECTION_STUDENT_PATIENTS)
        .subscribe((snapshot) => {
          this.patientSignal.set(this.patientService.getPatientFromSnapshot(snapshot));
        });
    });

    // When patient loads, fetch in-stock provider orders
    effect(() => {
      const patient = this.patientSignal();
      if (!patient) return;

      this.containersService.inStockProviderOrdersIds(patient.providerOrders)
        .subscribe(ids => {
          this.inStockProviderOrdersIdsSignal.set(ids);
        });
    });
  }

  /** Returns the query param that indicates any provider orders to release beyond the first */
  getMoreProviderOrderIds(ids: string[]) {
    return { [PATH_MORE_PROVIDER_ORDER_IDS]: ids.slice(1) };
  }
}